import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth";
import { SessionService } from "@/features/session";
import { DEFAULT_AGENT_ID } from "@/config";
import { getPrrProjectId, isPrrMode } from "@/prr";

const PRR_SESSION_LIST_SCOPE = "prr-user";

export function getSessionListScope(projectId: string | undefined): string {
  return isPrrMode() ? PRR_SESSION_LIST_SCOPE : projectId || "";
}

export const sessionKeys = {
  all: ["sessions"] as const,
  lists: () => [...sessionKeys.all, "list"] as const,
  list: (scope: string) => [...sessionKeys.lists(), scope] as const,
  details: () => [...sessionKeys.all, "detail"] as const,
  detail: (projectScope: string, sessionId: string) =>
    [...sessionKeys.details(), projectScope, sessionId] as const,
};

export function useSessionsQuery(projectId: string | undefined) {
  const scope = getSessionListScope(projectId);

  return useQuery({
    queryKey: sessionKeys.list(scope),
    queryFn: async () => {
      if (isPrrMode()) {
        const response = await SessionService.getUserSessions();
        return response.sessions;
      }

      return SessionService.getProjectSessions(projectId!);
    },
    enabled: isPrrMode() || !!projectId,
  });
}

export function useSessionQuery(
  projectId: string | undefined,
  sessionId: string | undefined,
) {
  const detailScope = getSessionListScope(projectId);

  return useQuery({
    queryKey: sessionKeys.detail(detailScope, sessionId!),
    queryFn: async () => {
      if (!sessionId) {
        throw new Error("sessionId is required");
      }

      if (projectId) {
        return SessionService.getSessionDetail(projectId, sessionId);
      }

      if (isPrrMode()) {
        const resolvedProjectId =
          await SessionService.findProjectIdBySessionId(sessionId);

        if (!resolvedProjectId) {
          throw new Error("Could not resolve project for session");
        }

        return SessionService.getSessionDetail(resolvedProjectId, sessionId);
      }

      throw new Error("projectId is required");
    },
    enabled: !!sessionId && (isPrrMode() || !!projectId),
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userProjectId = getPrrProjectId(user);

  return useMutation({
    mutationFn: async ({
      projectId,
      name,
    }: {
      projectId?: string;
      name?: string;
    }) => {
      let resolvedProjectId = projectId;

      if (isPrrMode()) {
        resolvedProjectId =
          projectId ||
          (await SessionService.getDefaultProjectIdFromSessions()) ||
          userProjectId;
      }

      if (!resolvedProjectId) {
        throw new Error("projectId is required to create session");
      }

      return SessionService.createSession({
        project_id: resolvedProjectId,
        name,
        agent_id: DEFAULT_AGENT_ID,
      });
    },
    onSuccess: (_, { projectId }) => {
      const scope = getSessionListScope(projectId);
      queryClient.invalidateQueries({ queryKey: sessionKeys.list(scope) });
    },
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      sessionId,
    }: {
      projectId: string;
      sessionId: string;
    }) => SessionService.deleteSession(projectId, sessionId),
    onSuccess: (_, { projectId }) => {
      const scope = getSessionListScope(projectId);
      queryClient.invalidateQueries({ queryKey: sessionKeys.list(scope) });
    },
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      sessionId,
      data,
    }: {
      projectId: string;
      sessionId: string;
      data: { name?: string };
    }) => SessionService.updateSession(projectId, sessionId, data),
    onSuccess: (_, { projectId, sessionId }) => {
      const scope = getSessionListScope(projectId);
      queryClient.invalidateQueries({
        queryKey: sessionKeys.detail(scope, sessionId),
      });
      queryClient.invalidateQueries({ queryKey: sessionKeys.list(scope) });
    },
  });
}
